import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";

type EmailBuilderSettingsAccordionContextValue = {
  expandedSectionIds: ReadonlySet<string>;
  toggleSection: (sectionId: string) => void;
  expandSection: (sectionId: string) => void;
  claimSectionDivider: () => boolean;
};

const EmailBuilderSettingsAccordionContext =
  createContext<EmailBuilderSettingsAccordionContextValue | null>(null);

export function useEmailBuilderSettingsAccordion(): EmailBuilderSettingsAccordionContextValue {
  const context = useContext(EmailBuilderSettingsAccordionContext);
  if (!context) {
    throw new Error(
      "EmailBuilderSettingsAccordion.Section must be used within EmailBuilderSettingsAccordion",
    );
  }
  return context;
}

type EmailBuilderSettingsAccordionProviderProps = {
  defaultExpandedSectionIds?: readonly string[];
  children: ReactNode;
};

export function EmailBuilderSettingsAccordionProvider(
  props: EmailBuilderSettingsAccordionProviderProps,
): React.ReactElement {
  const { defaultExpandedSectionIds = [], children } = props;
  const sectionRenderIndexRef = useRef(0);
  sectionRenderIndexRef.current = 0;
  const [expandedSectionIds, setExpandedSectionIds] = useState<ReadonlySet<string>>(
    () => new Set(defaultExpandedSectionIds),
  );

  const claimSectionDivider = useCallback((): boolean => {
    const index = sectionRenderIndexRef.current;
    sectionRenderIndexRef.current += 1;
    return index > 0;
  }, []);

  const toggleSection = useCallback((sectionId: string) => {
    setExpandedSectionIds(previous => {
      const next = new Set(previous);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  }, []);

  const expandSection = useCallback((sectionId: string) => {
    setExpandedSectionIds(previous => {
      if (previous.has(sectionId)) {
        return previous;
      }
      const next = new Set(previous);
      next.add(sectionId);
      return next;
    });
  }, []);

  const value = useMemo(
    (): EmailBuilderSettingsAccordionContextValue => ({
      expandedSectionIds,
      toggleSection,
      expandSection,
      claimSectionDivider,
    }),
    [claimSectionDivider, expandSection, expandedSectionIds, toggleSection],
  );

  return (
    <EmailBuilderSettingsAccordionContext.Provider value={value}>
      {children}
    </EmailBuilderSettingsAccordionContext.Provider>
  );
}
